import React, { useState, useMemo, useImperativeHandle, forwardRef } from 'react';
import { DrawingCanvas } from './DrawingCanvas';
import { FourierAIPanel } from './FourierAIPanel';
import { generatePresetWave, computeDFT } from '../../utils/math';
import { useCanvasAnimation } from '../../hooks/useCanvasAnimation';
import { useAudio } from '../../hooks/useAudio';
import { RangeSlider } from '../ui/RangeSlider';
import { Wave, COLORS, DFTCoefficient } from '../../types';
import analyticsService from '../../services/analyticsService';

interface FourierLabProps {
    onExportToLab?: (waves: Wave[]) => void;
    importedWaves?: Wave[] | null;
    onImportProcessed?: () => void;
}

export interface FourierLabHandle {
    renderControls: () => React.ReactElement;
}

interface FourierControlsProps {
    harmonics: number;
    setHarmonics: (value: number) => void;
    baseFrequency: number;
    setBaseFrequency: (value: number) => void;
    brightnessTilt: number;
    setBrightnessTilt: (value: number) => void;
    textureNoise: number;
    setTextureNoise: (value: number) => void;
    brushSize: number;
    setBrushSize: (value: number) => void;
    zoom: number;
    setZoom: (value: number) => void;
    pan: number;
    setPan: (value: number) => void;
    showComponents: boolean;
    setShowComponents: (value: boolean) => void;
    manualCoefficients: DFTCoefficient[] | null;
    importScaleFactor: number | null;
    importedColorByFreq: Record<number, string>;
    importedHzByK: Record<number, number> | null;
    hasManuallyEditedComponents: boolean;
    setHasManuallyEditedComponents: (value: boolean) => void;
    updateComponent: (freq: number, newAmpWaveScale: number, newPhaseDeg: number) => void;
    updateComponentFrequency: (oldFreq: number, newFreq: number) => void;
    removeComponent: (freq: number) => void;
    addComponent: (freq: number) => void;
    reconstructDrawingFromCoeffs: (coeffs: DFTCoefficient[]) => void;
    setManualCoefficients: (coeffs: DFTCoefficient[] | null) => void;
    setImportScaleFactor: (value: number | null) => void;
    setImportedFreqs: (value: Set<number> | null) => void;
    setImportedHzByK: (value: Record<number, number> | null) => void;
    setDrawing: (drawing: number[]) => void;
    handleExport: () => void;
    coefficients: DFTCoefficient[];
}

export const FourierLab = forwardRef<FourierLabHandle, FourierLabProps>(
    ({ onExportToLab, importedWaves, onImportProcessed }, ref) => {
    // 200 sample points for the drawing
    const [drawing, setDrawing] = useState<number[]>(new Array(200).fill(0.5));
    const [harmonics, setHarmonics] = useState<number>(5);
    const [showComponents, setShowComponents] = useState<boolean>(false);
    
    // Allow manual override of coefficients from imported waves
    const [manualCoefficients, setManualCoefficients] = useState<DFTCoefficient[] | null>(null);
    // Track if user has manually edited components (only then do we lock coefficients)
    const [hasManuallyEditedComponents, setHasManuallyEditedComponents] = useState<boolean>(false);
    // Track imported frequencies and scaling to ensure 1:1 export
    const [importedFreqs, setImportedFreqs] = useState<Set<number> | null>(null);
    const [importScaleFactor, setImportScaleFactor] = useState<number | null>(null);
    const [importedColorByFreq, setImportedColorByFreq] = useState<Record<number, string>>({});
    const [importedHzByK, setImportedHzByK] = useState<Record<number, number> | null>(null);
    
    // Editor controls
    const [brushSize, setBrushSize] = useState<number>(5);
    const [zoom, setZoom] = useState<number>(1);
    const [pan, setPan] = useState<number>(0);
    const [baseFrequency, setBaseFrequency] = useState<number>(220);
    const [brightnessTilt, setBrightnessTilt] = useState<number>(0); // -1 (darker) to 1 (brighter)
    const [textureNoise, setTextureNoise] = useState<number>(0); // 0 to 0.25 noise layer

    // Compute coefficients only when drawing changes, or use manual ones from import
    const coefficients = useMemo(() => {
        if (manualCoefficients) return manualCoefficients;
        return computeDFT(drawing);
    }, [drawing, manualCoefficients]);

    const applyToneControls = (coeffs: DFTCoefficient[]): DFTCoefficient[] => {
        const maxK = Math.max(...coeffs.map(c => c.freq), 1);
        const tiltStrength = 1.1; // modest tilt
        const tilt = brightnessTilt;

        const tilted = coeffs.map(c => {
            const normK = maxK > 1 ? (c.freq - 1) / (maxK - 1) : 0;
            const weight = Math.exp(tiltStrength * tilt * (normK - 0.5));
            return { ...c, amp: c.amp * weight } as DFTCoefficient;
        });

        if (textureNoise <= 0) return tilted;

        const baseAmp = tilted.length > 0 ? tilted.reduce((s, c) => s + c.amp, 0) / tilted.length : 0;
        const noiseHarmonics = Math.max(2, Math.min(6, harmonics));
        const noise: DFTCoefficient[] = [];
        for (let i = 0; i < noiseHarmonics; i++) {
            const k = Math.max(1, Math.round((i + 1) * (maxK / noiseHarmonics)));
            // pseudo-random phase but deterministic
            const phase = ((k * 57) % 360) * Math.PI / 180;
            noise.push({ freq: k, amp: baseAmp * textureNoise * 0.6, phase });
        }
        return [...tilted, ...noise];
    };

    // Generate audio waves from coefficients for playback
    const audioWaves = useMemo(() => {
        const waves: Wave[] = [];
        if (manualCoefficients && importScaleFactor !== null) {
            const coeffs = applyToneControls(manualCoefficients.slice(1));
            let idCounter = 1;
            coeffs.forEach((c, index) => {
                if (c.freq > harmonics || c.amp <= 0) return;
                const k = c.freq;
                const freqHz = importedHzByK && importedHzByK[k] ? importedHzByK[k] : k * baseFrequency;
                waves.push({
                    id: idCounter++,
                    freq: freqHz,
                    amp: c.amp * importScaleFactor,
                    phase: (c.phase * 180 / Math.PI + 360) % 360,
                    color: importedColorByFreq[c.freq] ?? COLORS[index % COLORS.length],
                    muted: false,
                });
            });
        } else {
            const activeCoefficients = applyToneControls(coefficients.slice(1, harmonics + 1));
            const maxAmp = Math.max(...activeCoefficients.map(c => c.amp));
            const scaleFactor = maxAmp > 0.001 ? (80 / maxAmp) : 100;
            let idCounter = 1;
            activeCoefficients.forEach((c, index) => {
                if (c.amp * scaleFactor < 1) return;
                waves.push({
                    id: idCounter++,
                    freq: c.freq * baseFrequency,
                    amp: c.amp * scaleFactor,
                    phase: (c.phase * 180 / Math.PI + 360) % 360,
                    color: COLORS[index % COLORS.length],
                    muted: false,
                });
            });
        }
        return waves;
    }, [coefficients, harmonics, manualCoefficients, importScaleFactor, importedFreqs, importedColorByFreq, baseFrequency, importedHzByK, brightnessTilt, textureNoise]);

    // Audio playback hook
    const { isPlaying, toggleAudio } = useAudio(audioWaves, 1);

    // Initialize manual coefficients from drawn wave if not already set
    React.useEffect(() => {
        if (hasManuallyEditedComponents || importedWaves) return;
        const hasDrawn = drawing.some(v => Math.abs(v - 0.5) > 0.05);
        if (hasDrawn) {
            const computedCoeffs = computeDFT(drawing);
            const maxAmp = Math.max(...computedCoeffs.slice(1).map(c => c.amp));
            const scaleFactor = maxAmp > 0.001 ? (80 / maxAmp) : 1;
            setManualCoefficients(computedCoeffs);
            setImportScaleFactor(scaleFactor);
            setShowComponents(true);
            setImportedFreqs(new Set(computedCoeffs.slice(1).filter(c => c.amp > 0).map(c => c.freq)));
        }
    }, [drawing, hasManuallyEditedComponents, importedWaves]);

    // Helper: reconstruct drawing from coefficients
    const reconstructDrawingFromCoeffs = (coeffs: DFTCoefficient[]) => {
        const newDrawing = new Array(200).fill(0);
        for (let i = 0; i < newDrawing.length; i++) {
            const t = i / (newDrawing.length - 1);
            let sum = 0;
            for (let k = 0; k < coeffs.length; k++) {
                const c = coeffs[k];
                sum += c.amp * Math.cos(2 * Math.PI * c.freq * t + c.phase);
            }
            newDrawing[i] = Math.max(0, Math.min(1, sum));
        }
        setDrawing(newDrawing);
    };

    // Handlers to tweak manual components
    const updateComponent = (freq: number, newAmpWaveScale: number, newPhaseDeg: number) => {
        if (!manualCoefficients || importScaleFactor === null) return;
        setHasManuallyEditedComponents(true); // Mark as manually edited
        const updated = manualCoefficients.map(c => {
            if (c.freq !== freq) return c;
            return {
                ...c,
                amp: Math.max(0, (newAmpWaveScale || 0) / importScaleFactor),
                phase: ((newPhaseDeg || 0) * Math.PI) / 180,
            };
        });
        setManualCoefficients(updated);
        reconstructDrawingFromCoeffs(updated);
    };

    const updateComponentFrequency = (oldFreq: number, newFreq: number) => {
        if (!manualCoefficients || importScaleFactor === null) return;
        if (newFreq < 1 || newFreq > harmonics) return; // Respect harmonic limit
        if (newFreq === oldFreq) return; // No change needed
        
        setHasManuallyEditedComponents(true); // Mark as manually edited
        
        // Allow fractional harmonics; prevent exact duplicate only if same index
        if (manualCoefficients.some(c => c.freq === newFreq && c.freq !== oldFreq)) {
            return;
        }

        const updated = manualCoefficients.map(c => {
            if (c.freq !== oldFreq) return c;
            return { ...c, freq: newFreq };
        });
        setManualCoefficients(updated);
        reconstructDrawingFromCoeffs(updated);
        
        // Update color mapping
        const color = importedColorByFreq[oldFreq];
        if (color) {
            setImportedColorByFreq(prev => {
                const next = { ...prev };
                delete next[oldFreq];
                next[newFreq] = color;
                return next;
            });
        }
        
        // Update frequency set
        if (importedFreqs) {
            const next = new Set(Array.from(importedFreqs));
            next.delete(oldFreq);
            next.add(newFreq);
            setImportedFreqs(next);
        }

        // Move preserved Hz mapping if present
        if (importedHzByK && importedHzByK[oldFreq] !== undefined) {
            setImportedHzByK(prev => {
                if (!prev) return prev;
                const next = { ...prev } as Record<number, number>;
                const val = next[oldFreq];
                delete next[oldFreq];
                next[newFreq] = val;
                return next;
            });
        }
    };

    const removeComponent = (freq: number) => {
        if (!manualCoefficients) return;
        setHasManuallyEditedComponents(true); // Mark as manually edited
        const updated = manualCoefficients.map(c => (c.freq === freq ? { ...c, amp: 0 } : c));
        setManualCoefficients(updated);
        reconstructDrawingFromCoeffs(updated);
        if (importedFreqs) {
            const next = new Set(Array.from(importedFreqs));
            next.delete(freq);
            setImportedFreqs(next);
        }
    };

    const addComponent = (freq: number) => {
        if (!manualCoefficients || importScaleFactor === null) return;
        // Prevent adding frequencies beyond the harmonic complexity limit
        if (freq > harmonics) return;
        setHasManuallyEditedComponents(true); // Mark as manually edited
        const exists = manualCoefficients.some(c => c.freq === freq);
        const nextCoeffs = exists
            ? manualCoefficients.map(c => (c.freq === freq ? { ...c, amp: 50 / importScaleFactor, phase: 0 } : c))
            : [...manualCoefficients, { freq, amp: 50 / importScaleFactor, phase: 0 }];
        setManualCoefficients(nextCoeffs);
        reconstructDrawingFromCoeffs(nextCoeffs);
        const color = importedColorByFreq[freq] ?? COLORS[freq % COLORS.length];
        setImportedColorByFreq(prev => ({ ...prev, [freq]: color }));
        if (importedFreqs) {
            const next = new Set(Array.from(importedFreqs));
            next.add(freq);
            setImportedFreqs(next);
        }
    };

    // Handle AI-generated audio coefficients or drawing path
    const handleAIResponse = (result: { coefficients?: DFTCoefficient[]; drawingPath?: number[]; description: string; baseHz?: number; brightnessTilt?: number; textureNoise?: number }) => {
        const { coefficients, drawingPath, description, baseHz, brightnessTilt: aiBrightness, textureNoise: aiTexture } = result;
        
        // If AI generated a drawing path, use it directly
        if (drawingPath && drawingPath.length > 0) {
            setDrawing(drawingPath);
            setManualCoefficients(null); // Let DFT analyze the drawing
            setHasManuallyEditedComponents(false);
        }
        
        if (baseHz && isFinite(baseHz) && baseHz > 0) {
            setBaseFrequency(Math.max(0.1, Math.min(2000, baseHz)));
        }
        if (aiBrightness !== undefined && isFinite(aiBrightness)) {
            setBrightnessTilt(Math.max(-1, Math.min(1, aiBrightness)));
        }
        if (aiTexture !== undefined && isFinite(aiTexture)) {
            setTextureNoise(Math.max(0, Math.min(0.25, aiTexture)));
        }
        
        // If coefficients mode, set manual coefficients
        if (coefficients && coefficients.length > 0) {
            setHasManuallyEditedComponents(true);
            
            // Find the max frequency and amplitude to set appropriate harmonics
            const maxFreq = Math.max(...coefficients.map(c => c.freq), 5);
            setHarmonics(Math.min(Math.max(maxFreq + 2, 10), 50)); // Set to at least maxFreq + 2, but limit to 50
            
            // Create full coefficient array with DC component at index 0
            const fullCoefficients: DFTCoefficient[] = [
                { freq: 0, amp: 0, phase: 0 } // DC component
            ];
        
            // Find max amplitude for scaling
            const maxAmp = Math.max(...coefficients.map(c => c.amp));
            const scaleFactor = maxAmp > 0 ? (80 / maxAmp) : 1;
            
            // Add all harmonics up to maxFreq, with AI coefficients where provided
            for (let k = 1; k <= maxFreq; k++) {
                const aiCoeff = coefficients.find(c => c.freq === k);
                if (aiCoeff) {
                    fullCoefficients.push({
                        freq: k,
                        amp: aiCoeff.amp / scaleFactor,
                        phase: (aiCoeff.phase * Math.PI) / 180,
                    });
                } else {
                    fullCoefficients.push({ freq: k, amp: 0, phase: 0 });
                }
            }
            
            setManualCoefficients(fullCoefficients);
            setImportScaleFactor(scaleFactor);
            setImportedFreqs(new Set(coefficients.map(c => c.freq)));
            setShowComponents(true);
            
            // Reconstruct the drawing from these coefficients
            reconstructDrawingFromCoeffs(fullCoefficients);
        }
        
        analyticsService.trackFeatureUse('fourier_ai_generate', description);
    };

    // Helper: Estimate base frequency from absolute Hz list
    const estimateBaseFrequency = (freqs: number[]) => {
        const cleaned = freqs.filter(f => isFinite(f) && f > 0).sort((a, b) => a - b);
        if (cleaned.length === 0) return { f0: baseFrequency, ks: [] as number[], error: 1 };
        const minHz = cleaned[0];
        const ks0 = cleaned.map(f => Math.max(1, Math.round(f / Math.max(1e-6, minHz))));
        const f0cand = cleaned.reduce((sum, f, i) => sum + f / ks0[i], 0) / cleaned.length;
        const ks1 = cleaned.map(f => Math.max(1, Math.round(f / Math.max(1e-6, f0cand))));
        const f0 = cleaned.reduce((sum, f, i) => sum + f / ks1[i], 0) / cleaned.length;
        const err = cleaned.reduce((sum, f, i) => {
            const approx = ks1[i] * f0;
            return sum + Math.abs(f - approx) / f;
        }, 0) / cleaned.length;
        return { f0, ks: ks1, error: err };
    };

    // Convert imported waves to DFT coefficients and drawing
    React.useEffect(() => {
        if (importedWaves && importedWaves.length > 0) {
            const activeWaves = importedWaves.filter(w => !w.muted && w.amp > 0);
            
            if (activeWaves.length > 0) {
                // Map Wave Lab absolute Hz to harmonic indices using estimated base frequency
                const freqsHz = activeWaves.map(w => w.freq);
                const { f0, error } = estimateBaseFrequency(freqsHz);
                const f0ToUse = (isFinite(f0) && f0 > 0) ? f0 : Math.min(...freqsHz);
                if (isFinite(f0) && f0 > 0 && error < 0.1) {
                    setBaseFrequency(f0);
                }

                const maxWaveAmp = Math.max(...activeWaves.map(w => w.amp));
                const scaleFactor = maxWaveAmp > 0 ? (80 / maxWaveAmp) : 1;
                setImportScaleFactor(scaleFactor);

                const mapped = activeWaves.map(w => ({ k: w.freq / Math.max(1e-6, f0ToUse), wave: w }));
                const colorMap: Record<number, string> = {};
                mapped.forEach(({ k, wave }) => { colorMap[k] = wave.color; });
                setImportedColorByFreq(colorMap);

                const maxK = Math.max(...mapped.map(m => m.k));
                setImportedFreqs(new Set(mapped.map(m => m.k)));

                // Preserve exact Hz for audio/export fidelity
                const hzMap: Record<number, number> = {};
                mapped.forEach(({ k, wave }) => { hzMap[k] = wave.freq; });
                setImportedHzByK(hzMap);

                const newCoefficients: DFTCoefficient[] = [{ freq: 0, amp: 0, phase: 0 }];
                mapped.forEach(({ k, wave }) => {
                    const dftAmp = wave.amp / scaleFactor;
                    const phaseRad = (wave.phase * Math.PI) / 180;
                    newCoefficients.push({ freq: k, amp: dftAmp, phase: phaseRad });
                });

                setManualCoefficients(newCoefficients);
                // Ensure the harmonics slider equals the number of non-DC components
                const nonDcCount = newCoefficients.slice(1).filter(c => c.amp > 0).length;
                setHarmonics(Math.min(50, Math.max(1, nonDcCount)));
                reconstructDrawingFromCoeffs(newCoefficients);
            }
            
            if (onImportProcessed) {
                onImportProcessed();
            }
        }
    }, [importedWaves, onImportProcessed]);

    // Updated signature: added deltaTime
    const reconstructionCanvasRef = useCanvasAnimation((ctx, time, deltaTime, width, height) => {
        ctx.clearRect(0, 0, width, height);
        
        // Draw ghost of original drawing
        ctx.beginPath();
        ctx.strokeStyle = 'rgba(200, 200, 200, 0.2)';
        ctx.lineWidth = 4;
        for (let i = 0; i < drawing.length; i++) {
            const x = (i / (drawing.length - 1)) * width;
            const y = (1 - drawing[i]) * height;
            if (i === 0) ctx.moveTo(x, y);
            else ctx.lineTo(x, y);
        }
        ctx.stroke();

        // Draw Individual Sine Components
        if (showComponents) {
            ctx.lineWidth = 1.5;
            for (let k = 0; k < harmonics; k++) {
                if (k >= coefficients.length) break;
                const c = coefficients[k];

                ctx.beginPath();
                // Assign distinct color based on harmonic index using Golden Angle approximation
                // This ensures adjacent harmonics have distinct hues
                ctx.strokeStyle = `hsla(${(k * 137.5) % 360}, 70%, 60%, 0.3)`;

                for (let x = 0; x < width; x+=2) {
                    const t = x / width;
                    let yVal = 0;
                    
                    if (k === 0) {
                         // DC Component: Constant level
                         yVal = c.amp;
                    } else {
                         // AC Components: Centered around 0.5 for clear visualization
                         yVal = 0.5 + c.amp * Math.cos(2 * Math.PI * c.freq * t + c.phase);
                    }

                    const screenY = (1 - yVal) * height;
                    if (x === 0) ctx.moveTo(x, screenY);
                    else ctx.lineTo(x, screenY);
                }
                ctx.stroke();
            }
        }

        // Draw Reconstruction Sum
        ctx.beginPath();
        ctx.strokeStyle = '#4F46E5'; // Indigo 600
        ctx.lineWidth = 3;
        
        // Add shadow/glow to make it pop over the components if they are visible
        if (showComponents) {
            ctx.shadowColor = '#000000';
            ctx.shadowBlur = 4;
        }

        for (let x = 0; x < width; x++) {
            const t = x / width; // 0 to 1
            let yVal = 0;

            for (let k = 0; k < harmonics; k++) {
                if (k >= coefficients.length) break;
                const c = coefficients[k];
                yVal += c.amp * Math.cos(2 * Math.PI * c.freq * t + c.phase);
            }
            
            const screenY = (1 - yVal) * height;

            if (x === 0) ctx.moveTo(x, screenY);
            else ctx.lineTo(x, screenY);
        }
        ctx.stroke();
        ctx.shadowBlur = 0;

    }, [drawing, harmonics, coefficients, showComponents]);

    const handleExport = () => {
        if (!onExportToLab) return;
        analyticsService.trackFourierStart();

        const wavesToExport: Wave[] = [];

        // If we have imported manual coefficients and import scaling, export exactly those waves
        if (manualCoefficients && importScaleFactor !== null) {
            const coeffs = manualCoefficients.slice(1); // skip DC
            let idCounter = 1;
            coeffs.forEach((c, index) => {
                // Only export frequencies that were originally imported
                if (importedFreqs && !importedFreqs.has(c.freq)) return;
                if (c.amp <= 0) return;
                const k = c.freq;
                const freqHz = importedHzByK && importedHzByK[k] ? importedHzByK[k] : k * baseFrequency;
                wavesToExport.push({
                    id: idCounter++,
                    freq: freqHz,
                    amp: c.amp * importScaleFactor,
                    phase: (c.phase * 180 / Math.PI + 360) % 360,
                    color: importedColorByFreq[c.freq] ?? COLORS[index % COLORS.length],
                    muted: false
                });
            });
        } else {
            // Fallback: derive waves from current drawing via computed DFT
            const activeCoefficients = coefficients.slice(1, harmonics + 1);
            const maxAmp = Math.max(...activeCoefficients.map(c => c.amp));
            const scaleFactor = maxAmp > 0.001 ? (80 / maxAmp) : 100;
            activeCoefficients.forEach((c, index) => {
                if (c.amp * scaleFactor < 1) return;
                wavesToExport.push({
                    id: index + 1,
                    freq: c.freq * baseFrequency,
                    amp: c.amp * scaleFactor,
                    phase: (c.phase * 180 / Math.PI + 360) % 360,
                    color: COLORS[index % COLORS.length],
                    muted: false
                });
            });
        }

        analyticsService.trackFourierExport('wave_lab');
        analyticsService.trackFeatureUse('fourier_export', `harmonics:${harmonics}_waves:${wavesToExport.length}`);
        
        onExportToLab(wavesToExport);
    };

    // Expose controls renderer to parent component via ref
    useImperativeHandle(ref, () => ({
        renderControls: () => (
            <FourierControls
                harmonics={harmonics}
                setHarmonics={setHarmonics}
                baseFrequency={baseFrequency}
                setBaseFrequency={setBaseFrequency}
                brightnessTilt={brightnessTilt}
                setBrightnessTilt={setBrightnessTilt}
                textureNoise={textureNoise}
                setTextureNoise={setTextureNoise}
                brushSize={brushSize}
                setBrushSize={setBrushSize}
                zoom={zoom}
                setZoom={setZoom}
                pan={pan}
                setPan={setPan}
                showComponents={showComponents}
                setShowComponents={setShowComponents}
                manualCoefficients={manualCoefficients}
                importScaleFactor={importScaleFactor}
                importedColorByFreq={importedColorByFreq}
                importedHzByK={importedHzByK}
                hasManuallyEditedComponents={hasManuallyEditedComponents}
                setHasManuallyEditedComponents={setHasManuallyEditedComponents}
                updateComponent={updateComponent}
                updateComponentFrequency={updateComponentFrequency}
                removeComponent={removeComponent}
                addComponent={addComponent}
                reconstructDrawingFromCoeffs={reconstructDrawingFromCoeffs}
                setManualCoefficients={setManualCoefficients}
                setImportScaleFactor={setImportScaleFactor}
                setImportedFreqs={setImportedFreqs}
                setImportedHzByK={setImportedHzByK}
                setDrawing={setDrawing}
                handleExport={handleExport}
                coefficients={coefficients}
            />
        )
    }), [
        harmonics, baseFrequency, brightnessTilt, textureNoise, brushSize, zoom, pan,
        showComponents, manualCoefficients, importScaleFactor, importedColorByFreq,
        importedHzByK, hasManuallyEditedComponents, coefficients
    ]);

    return (
        <div className="space-y-6 animate-fade-in">
            <div className="bg-white dark:bg-slate-800 dark:border-slate-700 rounded-xl shadow-md border border-gray-300 p-6">
                <div className="mb-6">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                            <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                            </svg>
                        </div>
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white">Fourier Drawing Lab</h2>
                    </div>
                    <p className="text-gray-600 dark:text-slate-400 text-sm max-w-2xl">
                        Draw any shape and watch it decompose into pure sine waves. The blue reconstruction shows how harmonics combine to recreate your drawing.
                    </p>
                </div>

                {/* AI Sound Designer Section */}
                <div className="mb-8">
                    <FourierAIPanel onAIResponse={handleAIResponse} />
                </div>

                {/* Audio Playback Button */}
                <div className="mb-8 flex justify-center">
                    <button
                        onClick={toggleAudio}
                        className={`flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-semibold transition-all shadow-md ${isPlaying
                            ? 'bg-blue-600 dark:bg-blue-700 text-white hover:bg-blue-700 dark:hover:bg-blue-800'
                            : 'bg-white dark:bg-slate-700 text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-600 border-2 border-gray-300 dark:border-slate-600'}`}
                        title={isPlaying ? 'Stop listening' : 'Listen to your waveform'}
                    >
                        {isPlaying ? (
                            <>
                                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                                </svg>
                                Playing
                            </>
                        ) : (
                            <>
                                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M8 5v14l11-7z" />
                                </svg>
                                Listen
                            </>
                        )}
                    </button>
                </div>

                <div className="grid lg:grid-cols-2 gap-8">
                    {/* Drawing Input */}
                    <div className="space-y-4">
                        <div className="flex justify-between items-center">
                            <div className="flex items-center gap-2">
                                <svg className="h-5 w-5 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                </svg>
                                <label className="text-sm font-bold text-gray-700 dark:text-slate-200">Draw Your Waveform</label>
                            </div>
                            <span className="text-xs text-gray-500 dark:text-slate-400">Click/drag to draw</span>
                        </div>

                        <DrawingCanvas 
                            data={drawing} 
                            onChange={(newDrawing) => {
                                setDrawing(newDrawing);
                            }} 
                            brushSize={brushSize}
                            zoom={zoom}
                            pan={pan}
                        />
                    </div>

                    {/* Harmonic Reconstruction Output */}
                    <div className="space-y-4">
                        <div className="flex items-center gap-2">
                            <svg className="h-5 w-5 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                            <label className="text-sm font-bold text-gray-700 dark:text-slate-200">Harmonic Reconstruction</label>
                        </div>

                        <label className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-slate-300 bg-gray-50 dark:bg-slate-700 rounded-lg cursor-pointer select-none hover:bg-gray-100 dark:hover:bg-slate-600 transition-colors border border-gray-300 dark:border-slate-600 w-fit">
                            <input 
                                type="checkbox" 
                                checked={showComponents} 
                                onChange={(e) => setShowComponents(e.target.checked)}
                                className="rounded border-gray-300 dark:border-slate-600 dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 focus:ring-indigo-500 w-4 h-4 cursor-pointer"
                            />
                            <span className="font-medium">Show Components</span>
                        </label>

                        {/* Canvas */}
                        <div className="h-64 md:h-80 w-full bg-gradient-to-br from-gray-900 to-gray-800 dark:from-slate-900 dark:to-slate-800 rounded-xl border-2 border-gray-700 dark:border-slate-700 shadow-lg overflow-hidden relative">
                             <canvas ref={reconstructionCanvasRef} className="w-full h-full block" />
                                <div className="absolute top-4 right-4 bg-black/50 backdrop-blur text-white text-xs px-2 py-1 rounded font-mono pointer-events-none">
                                  Components = {(manualCoefficients ? manualCoefficients.slice(1) : coefficients.slice(1)).filter(c => c.amp > 0).length}
                                </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});

FourierLab.displayName = 'FourierLab';

// Export separate control panel component for the right sidebar
export const FourierControls: React.FC<FourierControlsProps> = ({
    harmonics,
    setHarmonics,
    baseFrequency,
    setBaseFrequency,
    brightnessTilt,
    setBrightnessTilt,
    textureNoise,
    setTextureNoise,
    brushSize,
    setBrushSize,
    zoom,
    setZoom,
    pan,
    setPan,
    showComponents,
    setShowComponents,
    manualCoefficients,
    importScaleFactor,
    importedColorByFreq,
    importedHzByK,
    hasManuallyEditedComponents,
    setHasManuallyEditedComponents,
    updateComponent,
    updateComponentFrequency,
    removeComponent,
    addComponent,
    reconstructDrawingFromCoeffs,
    setManualCoefficients,
    setImportScaleFactor,
    setImportedFreqs,
    setImportedHzByK,
    setDrawing,
    handleExport,
    coefficients
}) => {
    return (
        <div className="space-y-4">
            {/* Editor Controls Section */}
            <div className="border-b border-gray-200 pb-4">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                    <svg className="h-4 w-4 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                    Drawing Tools
                </h3>
                <div className="space-y-3">
                    <RangeSlider 
                        label="Brush Size" 
                        min={1} max={20} step={1} 
                        value={brushSize} 
                        onChange={(e) => setBrushSize(parseInt(e.target.value))} 
                        color="#6366f1"
                    />
                    <RangeSlider 
                        label="Zoom" 
                        min={1} max={5} step={0.1} 
                        value={zoom} 
                        onChange={(e) => setZoom(parseFloat(e.target.value))} 
                        color="#6366f1"
                    />
                    <div className={`transition-opacity ${zoom <= 1 ? 'opacity-30 pointer-events-none' : 'opacity-100'}`}>
                        <RangeSlider 
                            label="Pan View" 
                            min={0} max={1} step={0.01} 
                            value={pan} 
                            onChange={(e) => setPan(parseFloat(e.target.value))} 
                            color="#6366f1"
                        />
                    </div>
                </div>
            </div>

            {/* Preset Buttons */}
            <div className="border-b border-gray-200 pb-4">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                    <svg className="h-4 w-4 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10\" />
                    </svg>
                    Presets
                </h3>
                <div className="flex flex-col gap-2">
                    <button 
                        onClick={() => {
                            setDrawing(generatePresetWave('square'));
                            setManualCoefficients(null);
                            setImportScaleFactor(null);
                            setImportedFreqs(null);
                            setImportedHzByK(null);
                            setHasManuallyEditedComponents(false);
                            analyticsService.trackFeatureUse('fourier_preset', 'square');
                        }}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-slate-300 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 rounded-lg transition-all hover:shadow-md border border-gray-300 dark:border-slate-600"
                    >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
                        </svg>
                        Square Wave
                    </button>
                    <button 
                        onClick={() => {
                            setDrawing(generatePresetWave('saw'));
                            setManualCoefficients(null);
                            setImportScaleFactor(null);
                            setImportedFreqs(null);
                            setImportedHzByK(null);
                            setHasManuallyEditedComponents(false);
                            analyticsService.trackFeatureUse('fourier_preset', 'sawtooth');
                        }}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-slate-300 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 rounded-lg transition-all hover:shadow-md border border-gray-300 dark:border-slate-600"
                    >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6\" />
                        </svg>
                        Sawtooth Wave
                    </button>
                    <button 
                        onClick={() => {
                            setDrawing(new Array(200).fill(0.5));
                            setManualCoefficients(null);
                            setImportScaleFactor(null);
                            setImportedFreqs(null);
                            setImportedHzByK(null);
                            setHasManuallyEditedComponents(false);
                            analyticsService.trackFeatureUse('fourier_reset');
                        }}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-all hover:shadow-md"
                    >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15\" />
                        </svg>
                        Reset
                    </button>
                </div>
            </div>

            {/* Audio Settings */}
            <div className="border-b border-gray-200 pb-4">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                    <svg className="h-4 w-4 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4\" />
                    </svg>
                    Sound Parameters
                </h3>

                <div className="space-y-3">
                    <div>
                        <label className="text-xs font-medium text-gray-600 dark:text-slate-400 block mb-1.5">Base Frequency (Hz)</label>
                        <input
                            type="number"
                            min={0.1}
                            max={2000}
                            step={0.1}
                            value={baseFrequency}
                            onChange={(e) => setBaseFrequency(parseFloat(e.target.value || '220'))}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg text-sm"
                        />
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-1.5">
                            <label className="text-xs font-medium text-gray-600 dark:text-slate-400">Brightness</label>
                            <span className="text-xs font-mono text-gray-500 dark:text-slate-400">{brightnessTilt.toFixed(2)}</span>
                        </div>
                        <input
                            type="range"
                            min={-1}
                            max={1}
                            step={0.05}
                            value={brightnessTilt}
                            onChange={(e) => setBrightnessTilt(parseFloat(e.target.value))}
                            className="w-full h-2 accent-indigo-500"
                            title="Tilt harmonic energy darker (-) or brighter (+)"
                        />
                        <div className="flex justify-between text-[10px] text-gray-400 dark:text-slate-500 mt-1">
                            <span>Darker</span>
                            <span>Brighter</span>
                        </div>
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-1.5">
                            <label className="text-xs font-medium text-gray-600 dark:text-slate-400">Texture</label>
                            <span className="text-xs font-mono text-gray-500 dark:text-slate-400">{(textureNoise * 100).toFixed(0)}%</span>
                        </div>
                        <input
                            type="range"
                            min={0}
                            max={0.25}
                            step={0.01}
                            value={textureNoise}
                            onChange={(e) => setTextureNoise(parseFloat(e.target.value))}
                            className="w-full h-2 accent-indigo-500"
                            title="Add a subtle noise/air layer"
                        />
                        <div className="flex justify-between text-[10px] text-gray-400 dark:text-slate-500 mt-1">
                            <span>Clean</span>
                            <span>Noisy</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Harmonic Complexity */}
            <div className="border-b border-gray-200 pb-4">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                    <svg className="h-4 w-4 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4\" />
                    </svg>
                    Harmonic Complexity
                </h3>
                <RangeSlider
                    label="Number of Sine Waves"
                    valueDisplay={harmonics}
                    min={1}
                    max={50}
                    step={1}
                    value={harmonics}
                    onChange={(e) => setHarmonics(parseInt(e.target.value))}
                    color="#6366f1"
                />
                <p className="text-xs text-gray-600 dark:text-slate-400 mt-2">
                    More harmonics = sharper edges. Watch how adding sine waves creates any shape!
                </p>
            </div>

            {/* Export Button */}
            <div className="pb-4">
                <button 
                    onClick={handleExport}
                    className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white px-6 py-3 rounded-lg text-sm font-bold shadow-lg transition-all hover:scale-105 active:scale-95"
                >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    <span>Open in Wave Lab</span>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                </button>
            </div>

            {/* Components Editor for 1:1 tweaking when waves are imported */}
            {manualCoefficients && importScaleFactor !== null && (
                <div className="border-t border-gray-200 pt-4">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                            <svg className="h-4 w-4 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h2a1 1 0 011 1v15a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm6 6a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H10a1 1 0 01-1-1v-9zm6-3a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V7z\" />
                            </svg>
                            Component Waves
                        </h3>
                        <div className="flex items-center gap-2">
                            <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${hasManuallyEditedComponents 
                                ? 'bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-200' 
                                : 'bg-emerald-100 dark:bg-emerald-900 text-emerald-800 dark:text-emerald-200'}`}>
                                <span className={`inline-block w-1.5 h-1.5 rounded-full ${hasManuallyEditedComponents ? 'bg-amber-600' : 'bg-emerald-600'}`}></span>
                                {hasManuallyEditedComponents ? 'Manual' : 'Auto'}
                            </div>
                            {hasManuallyEditedComponents && (
                                <button
                                    onClick={() => {
                                        if (manualCoefficients) {
                                            reconstructDrawingFromCoeffs(manualCoefficients);
                                        }
                                        setHasManuallyEditedComponents(false);
                                    }}
                                    className="text-[10px] px-1.5 py-0.5 bg-amber-50 dark:bg-amber-900/30 hover:bg-amber-100 dark:hover:bg-amber-900/50 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800 rounded transition-colors"
                                >
                                    Unlock
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                        {manualCoefficients.slice(1).filter(c => c.amp > 0 && c.freq <= harmonics).map((c, idx) => {
                            const freq = c.freq;
                            const waveAmp = Math.round(c.amp * importScaleFactor);
                            const phaseDeg = Math.round((c.phase * 180) / Math.PI);
                            const color = importedColorByFreq[freq] ?? COLORS[idx % COLORS.length];
                            return (
                                <div key={freq} className="flex items-center gap-2 p-2 border border-gray-300 dark:border-slate-600 rounded-lg text-xs">
                                    <div className="w-3 h-3 rounded flex-shrink-0" style={{ backgroundColor: color }}></div>
                                    <label className="text-[10px] text-gray-600 dark:text-slate-400">k</label>
                                    <input
                                        type="number"
                                        min={1}
                                        max={harmonics}
                                        step={0.01}
                                        value={freq}
                                        onChange={e => updateComponentFrequency(freq, parseFloat(e.target.value || '0'))}
                                        className="w-14 px-1 py-0.5 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded text-[11px]"
                                    />
                                    <span className="text-[10px] text-gray-500 dark:text-slate-400">≈ {((importedHzByK && importedHzByK[freq]) ? importedHzByK[freq] : (freq * baseFrequency)).toFixed(0)} Hz</span>
                                    <label className="text-[10px] text-gray-600 dark:text-slate-400">A</label>
                                    <input
                                        type="number"
                                        min={0}
                                        max={100}
                                        value={waveAmp}
                                        onChange={e => updateComponent(freq, parseInt(e.target.value || '0', 10), phaseDeg)}
                                        className="w-12 px-1 py-0.5 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded text-[11px]"
                                    />
                                    <label className="text-[10px] text-gray-600 dark:text-slate-400">φ</label>
                                    <input
                                        type="number"
                                        min={0}
                                        max={360}
                                        value={phaseDeg}
                                        onChange={e => updateComponent(freq, waveAmp, parseInt(e.target.value || '0', 10))}
                                        className="w-12 px-1 py-0.5 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded text-[11px]"
                                    />
                                    <button
                                        onClick={() => removeComponent(freq)}
                                        className="ml-auto text-[10px] px-1.5 py-0.5 bg-red-50 dark:bg-red-900 hover:bg-red-100 dark:hover:bg-red-800 text-red-700 dark:text-red-200 border border-red-200 dark:border-red-800 rounded"
                                    >×</button>
                                </div>
                            );
                        })}
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                        <input
                            type="number"
                            min={1}
                            max={harmonics}
                            placeholder="k"
                            className="w-16 px-2 py-1 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded text-xs"
                            onKeyDown={e => {
                                if (e.key === 'Enter') {
                                    const target = e.target as HTMLInputElement;
                                    const k = parseInt(target.value || '0', 10);
                                    if (k > 0 && k <= harmonics) addComponent(k);
                                    target.value = '';
                                }
                            }}
                        />
                        <button
                            onClick={() => {
                                const nextFreq = (manualCoefficients.slice(1).filter(c => c.freq <= harmonics && c.amp > 0).at(-1)?.freq || 0) + 1;
                                if (nextFreq <= harmonics) addComponent(nextFreq);
                            }}
                            className="flex-1 text-xs px-2 py-1 bg-indigo-50 dark:bg-indigo-900 hover:bg-indigo-100 dark:hover:bg-indigo-800 text-indigo-700 dark:text-indigo-200 border border-indigo-200 dark:border-indigo-800 rounded"
                        >+ Add Next</button>
                    </div>
                </div>
            )}
        </div>
    );
};

FourierLab.displayName = 'FourierLab';